/**
 * CanvasEditor — Convex-backed canvas with full interaction support.
 * Feature parity with Tauri CanvasEditor.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { useCanvasStore } from '@/store/canvasStore';
import { DEFAULT_CANVAS_STATE, CANVAS_SIZE_PRESETS } from '@/types/canvas';
import type { CanvasState, CanvasLayer } from '@/types/canvas';
import { 
  Undo, Redo, ZoomIn, ZoomOut, Type, Image as ImageIcon, 
  Square, Grid3X3, Magnet, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  Lock, Unlock, Layers
} from 'lucide-react';
import { AssetImage } from '@/components/AssetImage';
import { cn } from '@/lib/utils';

const saveDebounceMs = 800;
const SNAP_THRESHOLD = 8;
const MIN_LAYER_SIZE = 8;

function fillToCssColor(fill: CanvasLayer['fill'], fallback = 'transparent') {
  if (typeof fill === 'string') return fill;
  if (fill?.type === 'solid' && fill.color) return fill.color;
  return fallback;
}

const defaultTextLayer = () => ({
  type: 'text' as const,
  name: 'Text',
  visible: true,
  locked: false,
  x: 100,
  y: 100,
  width: 200,
  height: 40,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
  opacity: 1,
  text_content: 'New text',
  text_style: { fontFamily: 'sans-serif', fontSize: 24, fill: '#ffffff' },
});

export function CanvasEditor() {
  const { currentProjectId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [isDragOver, setIsDragOver] = useState(false);
  const [spaceKeyDown, setSpaceKeyDown] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [showLayersPanel, setShowLayersPanel] = useState(true);
  const [guideLines, setGuideLines] = useState<{ vertical: number[]; horizontal: number[] } | null>(null);
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [layerStart, setLayerStart] = useState({ x: 0, y: 0 });
  
  // Resize state
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [resizeMouseStart, setResizeMouseStart] = useState<{ clientX: number; clientY: number } | null>(null);
  
  // Pan state
  const [panStart, setPanStart] = useState<{ scrollLeft: number; scrollTop: number; clientX: number; clientY: number } | null>(null);

  const {
    canvasState,
    selectedLayerId,
    zoom,
    showGrid,
    snapToGrid: snapEnabled,
    loadProject,
    getCanvasState,
    selectLayer,
    addLayer,
    deleteLayer,
    updateLayer,
    setZoom,
    updateCanvasSize,
    undo,
    redo,
    canUndo,
    canRedo,
    saveToHistory,
    toggleLayerVisibility,
    toggleLayerLock,
    moveLayer,
  } = useCanvasStore();
  
  const project = useQuery(
    api.projects.get,
    currentProjectId ? { accountId, id: currentProjectId } : 'skip'
  );
  const assets = useQuery(api.assets.list, { accountId });
  const updateProject = useMutation(api.projects.update);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!project) return;
    const cs = project.canvas_state as CanvasState | null | undefined;
    const state: CanvasState =
      cs && typeof cs === 'object' && Array.isArray(cs?.layers)
        ? (cs as CanvasState)
        : DEFAULT_CANVAS_STATE;
    loadProject(state);
  }, [project?._id, loadProject]);

  const scheduleSave = useCallback(() => {
    if (!currentProjectId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const state = getCanvasState();
      updateProject({
        accountId,
        id: currentProjectId,
        canvas_state: state,
      }).catch(console.error);
      saveTimerRef.current = null;
    }, saveDebounceMs);
  }, [currentProjectId, getCanvasState, updateProject]);

  useEffect(() => {
    if (!currentProjectId || !project) return;
    scheduleSave();
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [canvasState, currentProjectId, project, scheduleSave]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      // Spacebar for pan mode
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setSpaceKeyDown(true);
      }
      
      // Undo/Redo
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      
      // Delete
      if ((e.code === 'Delete' || e.code === 'Backspace') && selectedLayerId && selectedLayerId !== 'background') {
        e.preventDefault();
        deleteLayer(selectedLayerId);
      }
    };
    
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setSpaceKeyDown(false);
        setIsPanning(false);
      }
    };
    
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [selectedLayerId, deleteLayer, undo, redo]);

  // Mouse handlers for layer dragging
  const handleMouseDown = useCallback((e: React.MouseEvent, layerId: string) => {
    if (spaceKeyDown) return; // Let container handle pan
    const layer = canvasState.layers.find((l: CanvasLayer) => l.id === layerId);
    if (!layer || layer.locked) return;
    
    e.stopPropagation();
    selectLayer(layerId);
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLayerStart({ x: layer.x, y: layer.y });
  }, [spaceKeyDown, canvasState.layers, selectLayer]);

  // Resize handle mousedown
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: string, layerId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const layer = canvasState.layers.find((l: CanvasLayer) => l.id === layerId);
    if (!layer || layer.locked || layer.id === 'background') return;
    
    setResizeHandle(handle);
    setResizeStart({ x: layer.x, y: layer.y, width: layer.width, height: layer.height });
    setResizeMouseStart({ clientX: e.clientX, clientY: e.clientY });
  }, [canvasState.layers]);

  // Mouse move handler
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle resize
    if (resizeHandle && resizeStart && resizeMouseStart && selectedLayerId) {
      const dx = (e.clientX - resizeMouseStart.clientX) / zoom;
      const dy = (e.clientY - resizeMouseStart.clientY) / zoom;
      const { x: sx, y: sy, width: sw, height: sh } = resizeStart;
      
      let x = sx, y = sy, w = sw, h = sh;
      
      if (resizeHandle === 'se') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
      } else if (resizeHandle === 'sw') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
        x = sx + sw - w;
      } else if (resizeHandle === 'ne') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        y = sy + sh - h;
      } else if (resizeHandle === 'nw') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        x = sx + sw - w;
        y = sy + sh - h;
      } else if (resizeHandle === 'e') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
      } else if (resizeHandle === 'w') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        x = sx + sw - w;
      } else if (resizeHandle === 's') {
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
      } else if (resizeHandle === 'n') {
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        y = sy + sh - h;
      }
      
      updateLayer(selectedLayerId, { x, y, width: w, height: h });
      return;
    }
    
    // Handle pan
    if (isPanning && panStart && containerRef.current) {
      containerRef.current.scrollLeft = panStart.scrollLeft + (panStart.clientX - e.clientX);
      containerRef.current.scrollTop = panStart.scrollTop + (panStart.clientY - e.clientY);
      return;
    }
    
    // Handle layer drag
    if (!isDragging || !selectedLayerId) return;
    
    const dx = (e.clientX - dragStart.x) / zoom;
    const dy = (e.clientY - dragStart.y) / zoom;
    
    let newX = layerStart.x + dx;
    let newY = layerStart.y + dy;
    
    const layer = canvasState.layers.find((l) => l.id === selectedLayerId);
    const cw = canvasState.width;
    const ch = canvasState.height;
    const verticalGuides: number[] = [];
    const horizontalGuides: number[] = [];

    if (snapEnabled && layer && layer.id !== 'background') {
      const lw = layer.width;
      const lh = layer.height;
      
      // Snap to canvas edges and center
      if (Math.abs(newX - 0) <= SNAP_THRESHOLD) {
        newX = 0;
        verticalGuides.push(0);
      } else if (Math.abs(newX + lw - cw) <= SNAP_THRESHOLD) {
        newX = cw - lw;
        verticalGuides.push(cw);
      } else if (Math.abs(newX + lw / 2 - cw / 2) <= SNAP_THRESHOLD) {
        newX = cw / 2 - lw / 2;
        verticalGuides.push(cw / 2);
      }
      
      if (Math.abs(newY - 0) <= SNAP_THRESHOLD) {
        newY = 0;
        horizontalGuides.push(0);
      } else if (Math.abs(newY + lh - ch) <= SNAP_THRESHOLD) {
        newY = ch - lh;
        horizontalGuides.push(ch);
      } else if (Math.abs(newY + lh / 2 - ch / 2) <= SNAP_THRESHOLD) {
        newY = ch / 2 - lh / 2;
        horizontalGuides.push(ch / 2);
      }
      
      setGuideLines(
        verticalGuides.length > 0 || horizontalGuides.length > 0
          ? { vertical: verticalGuides, horizontal: horizontalGuides }
          : null
      );
      
      if (verticalGuides.length === 0) newX = Math.round(newX / 20) * 20;
      if (horizontalGuides.length === 0) newY = Math.round(newY / 20) * 20;
    } else {
      setGuideLines(null);
      if (snapEnabled) {
        newX = Math.round(newX / 20) * 20;
        newY = Math.round(newY / 20) * 20;
      }
    }
    
    updateLayer(selectedLayerId, { x: newX, y: newY });
  }, [isDragging, isPanning, resizeHandle, dragStart, layerStart, panStart, selectedLayerId, zoom, snapEnabled, canvasState.layers, canvasState.width, canvasState.height, updateLayer]);

  const handleMouseUp = useCallback(() => {
    if (isDragging || resizeHandle) {
      saveToHistory();
    }
    setIsDragging(false);
    setIsPanning(false);
    setResizeHandle(null);
    setResizeStart(null);
    setResizeMouseStart(null);
    setGuideLines(null);
  }, [isDragging, resizeHandle, saveToHistory]);

  // Document-level mouse handlers for resize
  useEffect(() => {
    if (!resizeHandle) return;
    
    const onMove = (e: MouseEvent) => {
      if (!resizeStart || !resizeMouseStart || !selectedLayerId) return;
      const dx = (e.clientX - resizeMouseStart.clientX) / zoom;
      const dy = (e.clientY - resizeMouseStart.clientY) / zoom;
      const { x: sx, y: sy, width: sw, height: sh } = resizeStart;
      
      let x = sx, y = sy, w = sw, h = sh;
      
      if (resizeHandle === 'se') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
      } else if (resizeHandle === 'sw') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
        x = sx + sw - w;
      } else if (resizeHandle === 'ne') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        y = sy + sh - h;
      } else if (resizeHandle === 'nw') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        x = sx + sw - w;
        y = sy + sh - h;
      } else if (resizeHandle === 'e') {
        w = Math.max(MIN_LAYER_SIZE, sw + dx);
      } else if (resizeHandle === 'w') {
        w = Math.max(MIN_LAYER_SIZE, sw - dx);
        x = sx + sw - w;
      } else if (resizeHandle === 's') {
        h = Math.max(MIN_LAYER_SIZE, sh + dy);
      } else if (resizeHandle === 'n') {
        h = Math.max(MIN_LAYER_SIZE, sh - dy);
        y = sy + sh - h;
      }
      
      updateLayer(selectedLayerId, { x, y, width: w, height: h });
    };
    
    const onUp = () => {
      setResizeHandle(null);
      setResizeStart(null);
      setResizeMouseStart(null);
      saveToHistory();
    };
    
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizeHandle, resizeStart, resizeMouseStart, selectedLayerId, zoom, updateLayer, saveToHistory]);

  // Container pan mousedown
  const handleContainerMouseDown = useCallback((e: React.MouseEvent) => {
    if (!spaceKeyDown) return;
    e.preventDefault();
    e.stopPropagation();
    setIsPanning(true);
    setPanStart({
      scrollLeft: containerRef.current?.scrollLeft ?? 0,
      scrollTop: containerRef.current?.scrollTop ?? 0,
      clientX: e.clientX,
      clientY: e.clientY,
    });
  }, [spaceKeyDown]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const assetId = e.dataTransfer.getData('application/asset-id');
    if (!assetId) return;

    const asset = assets?.find((a: any) => a._id === assetId);
    if (!asset) return;

    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) {
      addLayer({
        type: 'image',
        name: asset.name,
        visible: true,
        locked: false,
        x: canvasState.width / 2 - 150,
        y: canvasState.height / 2 - 150,
        width: asset.width ?? 300,
        height: asset.height ?? 300,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        asset_id: assetId,
      });
      return;
    }

    const x = (e.clientX - canvasRect.left) / zoom - (asset.width ?? 300) / 2;
    const y = (e.clientY - canvasRect.top) / zoom - (asset.height ?? 300) / 2;

    addLayer({
      type: 'image',
      name: asset.name,
      visible: true,
      locked: false,
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: asset.width ?? 300,
      height: asset.height ?? 300,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      asset_id: assetId,
    });
  }, [addLayer, assets, canvasState.width, canvasState.height, zoom]);

  // Helper functions
  const handleAddText = () => {
    addLayer(defaultTextLayer());
  };

  const handleAddImage = () => {
    addLayer({
      type: 'image',
      name: 'Image Layer',
      visible: true,
      locked: false,
      x: canvasState.width / 2 - 150,
      y: canvasState.height / 2 - 150,
      width: 300,
      height: 300,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
    });
  };

  const handleAddShape = () => {
    addLayer({
      type: 'shape',
      name: 'Shape Layer',
      visible: true,
      locked: false,
      x: canvasState.width / 2 - 100,
      y: canvasState.height / 2 - 100,
      width: 200,
      height: 200,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      fill: '#6366f1',
    });
  };

  const w = canvasState.width;
  const h = canvasState.height;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toolbar */}
      <div className="h-12 shrink-0 border-b border-border bg-card flex items-center px-4 justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleAddText} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors" title="Add Text">
            <Type className="h-4 w-4" />
            Text
          </button>
          <button onClick={handleAddImage} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors" title="Add Image">
            <ImageIcon className="h-4 w-4" />
            Image
          </button>
          <button onClick={handleAddShape} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors" title="Add Shape">
            <Square className="h-4 w-4" />
            Shape
          </button>
          <div className="w-px h-6 bg-border mx-2" />
          <button onClick={undo} disabled={!canUndo()} className="p-1.5 rounded-md hover:bg-muted disabled:opacity-30 transition-colors" title="Undo (Ctrl+Z)">
            <Undo className="h-4 w-4" />
          </button>
          <button onClick={redo} disabled={!canRedo()} className="p-1.5 rounded-md hover:bg-muted disabled:opacity-30 transition-colors" title="Redo (Ctrl+Shift+Z)">
            <Redo className="h-4 w-4" />
          </button>
          <div className="w-px h-6 bg-border mx-1" />
          <button onClick={() => selectedLayerId && selectedLayerId !== 'background' && deleteLayer(selectedLayerId)} disabled={!selectedLayerId || selectedLayerId === 'background'} className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 transition-colors" title="Delete Layer (Delete key)">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setShowLayersPanel(!showLayersPanel)} className={cn("flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors", showLayersPanel ? "bg-primary/20 text-primary" : "bg-muted hover:bg-muted/80")} title="Toggle Layers Panel">
            <Layers className="h-4 w-4" />
            Layers
          </button>
          <div className="w-px h-6 bg-border mx-2" />
          <button onClick={() => {}} className={cn("p-1.5 rounded-md transition-colors", showGrid ? "bg-primary/20 text-primary" : "hover:bg-muted")} title="Toggle Grid">
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button onClick={() => {}} className={cn("p-1.5 rounded-md transition-colors", snapEnabled ? "bg-primary/20 text-primary" : "hover:bg-muted")} title="Snap to Grid">
            <Magnet className="h-4 w-4" />
          </button>
          <div className="w-px h-6 bg-border mx-2" />
          <button onClick={() => setZoom(zoom - 0.1)} disabled={zoom <= 0.1} className="p-1.5 rounded-md hover:bg-muted disabled:opacity-30 transition-colors">
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-xs w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(zoom + 0.1)} disabled={zoom >= 5} className="p-1.5 rounded-md hover:bg-muted disabled:opacity-30 transition-colors">
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto p-4 bg-muted/30 relative"
          style={{ cursor: spaceKeyDown ? (isPanning ? 'grabbing' : 'grab') : undefined }}
          onMouseDown={handleContainerMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="min-h-full min-w-full flex items-center justify-center">
            <div
              ref={canvasRef}
              className={cn("relative shadow-2xl transition-all", isDragOver && "ring-2 ring-primary ring-offset-2")}
              style={{
                width: w * zoom,
                height: h * zoom,
                backgroundColor: canvasState.backgroundColor,
                backgroundImage: showGrid 
                  ? `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`
                  : 'none',
                backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
              }}
              onClick={() => selectLayer(null)}
            >
              {isDragOver && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
                  <div className="bg-primary/20 border-2 border-dashed border-primary rounded-lg px-6 py-4">
                    <ImageIcon className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium text-primary">Drop asset here</p>
                  </div>
                </div>
              )}
              
              {canvasState.layers
                .sort((a: CanvasLayer, b: CanvasLayer) => a.z_index - b.z_index)
                .map((layer) => (
                  <CanvasLayerComponent
                    key={layer.id}
                    layer={layer}
                    zoom={zoom}
                    isSelected={layer.id === selectedLayerId}
                    onMouseDown={(e) => handleMouseDown(e, layer.id)}
                    onResizeMouseDown={handleResizeMouseDown}
                  />
                ))}
              
              {/* Guide lines */}
              {guideLines && guideLines.vertical.map((x, i) => (
                <div key={`v-${i}`} className="absolute top-0 bottom-0 border-l border-dashed border-primary pointer-events-none z-40" style={{ left: x * zoom }} />
              ))}
              {guideLines && guideLines.horizontal.map((y, i) => (
                <div key={`h-${i}`} className="absolute left-0 right-0 border-t border-dashed border-primary pointer-events-none z-40" style={{ top: y * zoom }} />
              ))}
            </div>
          </div>
        </div>

        {/* Layers Panel */}
        {showLayersPanel && (
          <div className="w-64 border-l border-border bg-card flex flex-col">
            <div className="p-3 border-b border-border">
              <h3 className="font-medium text-sm">Layers</h3>
            </div>
            <div className="flex-1 overflow-auto p-2 space-y-1">
              {[...canvasState.layers].reverse().map((layer: CanvasLayer, index: number) => {
                const reverseIndex = canvasState.layers.length - 1 - index;
                const canMoveUp = reverseIndex < canvasState.layers.length - 1 && layer.id !== 'background';
                const canMoveDown = reverseIndex > 1;
                
                return (
                  <div
                    key={layer.id}
                    onClick={() => selectLayer(layer.id)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer",
                      selectedLayerId === layer.id ? "bg-primary/15 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                      className="p-1 rounded hover:bg-muted/50"
                    >
                      {layer.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <span className="flex-1 truncate">{layer.name}</span>
                    <div className="flex items-center gap-0.5">
                      <button onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'up'); }} disabled={!canMoveUp} className="p-1 rounded hover:bg-muted/50 disabled:opacity-30">
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveLayer(layer.id, 'down'); }} disabled={!canMoveDown} className="p-1 rounded hover:bg-muted/50 disabled:opacity-30">
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Canvas Layer Component with resize handles
function CanvasLayerComponent({
  layer,
  zoom,
  isSelected,
  onMouseDown,
  onResizeMouseDown,
}: {
  layer: CanvasLayer;
  zoom: number;
  isSelected: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onResizeMouseDown: (e: React.MouseEvent, handle: string, layerId: string) => void;
}) {
  const isBackground = layer.id === 'background';
  
  return (
    <div
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        left: layer.x * zoom,
        top: layer.y * zoom,
        width: layer.width * zoom,
        height: layer.height * zoom,
        transform: `rotate(${layer.rotation}deg) scale(${layer.scaleX}, ${layer.scaleY})`,
        opacity: layer.opacity,
        backgroundColor: fillToCssColor(layer.fill),
        border: isSelected ? '2px solid hsl(var(--primary))' : layer.type === 'background' ? 'none' : '1px solid rgba(255,255,255,0.1)',
        cursor: isBackground ? 'default' : 'move',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: layer.text_style?.fontSize ? `${(layer.text_style.fontSize * zoom) / (layer.scaleX || 1)}px` : '14px',
        fontFamily: layer.text_style?.fontFamily ?? 'sans-serif',
        color: layer.text_style?.fill ?? '#fff',
        pointerEvents: isBackground ? 'none' : 'auto',
      }}
    >
      {layer.type === 'text' && (layer.text_content ?? 'Text')}
      {layer.type === 'image' && layer.asset_id && (
        <AssetImage id={layer.asset_id} alt={layer.name} className="w-full h-full object-contain" />
      )}
      
      {/* Resize handles */}
      {isSelected && !isBackground && !layer.locked && (
        <>
          {['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'].map((handle) => (
            <div
              key={handle}
              onMouseDown={(e) => onResizeMouseDown(e, handle, layer.id)}
              className="absolute w-2 h-2 bg-primary border border-white rounded-sm"
              style={{
                top: handle.includes('n') ? -4 : handle.includes('s') ? 'calc(100% - 4px)' : 'calc(50% - 4px)',
                left: handle.includes('w') ? -4 : handle.includes('e') ? 'calc(100% - 4px)' : 'calc(50% - 4px)',
                cursor: `${handle}-resize`,
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
